// Importing the createSlice function from Redux Toolkit, which is used to create a slice of the Redux state.
import { createSlice } from "@reduxjs/toolkit";

// Initial state object defining the default values for the animation-related state properties.
const initialState = {
  showFreeSpinIntro: false,
  showFreeSpinInfo: false,
  showFreeSpinOutro: false,
  freeSpinIntroAnimationStarted: false, // Indicates if the free spin intro animation has started.
  freeSpinIntroAnimationCompleted: false, // Indicates if the free spin intro animation has completed.
  freeSpinInfoAnimationStarted: false, // Indicates if the free spin info animation has started.
  freeSpinInfoAnimationCompleted: false, // Indicates if the free spin info animation has completed.
  freeSpinOutroAnimationStarted: false, // Indicates if the free spin outro animation has started.
  freeSpinOutroAnimationCompleted: false, // Indicates if the free spin outro animation has completed.
  triggerFreeSpinIntroOutPhase: false, // Flag to trigger the transition out phase of the free spin intro.
  triggerFreeSpinInfoOutPhase: false, // Flag to trigger the transition out phase of the free spin info.
  triggerFreeSpinOutroOutPhase: false, // Flag to trigger the transition out phase of the free spin outro.
  bigWinAnimationStarted: false, // Indicates if the big win animation has started.
  bigWinAnimationCompleted: false, // Indicates if the big win animation has completed.
};

// Creating a slice named 'animations' with the initial state and reducers to handle state changes.
const animationsSlice = createSlice({
  name: "animations", // Name of the slice, used in action types.
  initialState, // Initial state of the slice.
  reducers: {
    // Reducer to set the show free spin intro state.
    setShowFreeSpinIntro: (state, action) => {
      state.showFreeSpinIntro = action.payload;
    },
    // Reducer to set the show free spin outro state.
    setShowFreeSpinOutro: (state, action) => {
      state.showFreeSpinOutro = action.payload;
    },
    // Reducer to set the show free spin info state.
    setShowFreeSpinInfo: (state, action) => {
      state.showFreeSpinInfo = action.payload;
    },
    // Reducer to set the free spin intro animation started state.
    setFreeSpinIntroAnimationStarted: (state, action) => {
      state.freeSpinIntroAnimationStarted = action.payload;
    },
    // Reducer to set the free spin intro animation completed state.
    setFreeSpinIntroAnimationCompleted: (state, action) => {
      state.freeSpinIntroAnimationCompleted = action.payload;
    },
    // Reducer to set the free spin info animation started state.
    setFreeSpinInfoAnimationStarted: (state, action) => {
      state.freeSpinInfoAnimationStarted = action.payload;
    },
    // Reducer to set the free spin info animation completed state.
    setFreeSpinInfoAnimationCompleted: (state, action) => {
      state.freeSpinInfoAnimationCompleted = action.payload;
    },
    // Reducer to set the free spin outro animation started state.
    setFreeSpinOutroAnimationStarted: (state, action) => {
      state.freeSpinOutroAnimationStarted = action.payload;
    },
    // Reducer to set the free spin outro animation completed state.
    setFreeSpinOutroAnimationCompleted: (state, action) => {
      state.freeSpinOutroAnimationCompleted = action.payload;
    },
    // Reducer to set the trigger for the free spin intro out phase.
    setTriggerFreeSpinIntroOutPhase: (state, action) => {
      state.triggerFreeSpinIntroOutPhase = action.payload;
    },
    // Reducer to set the trigger for the free spin info out phase.
    setTriggerFreeSpinInfoOutPhase: (state, action) => {
      state.triggerFreeSpinInfoOutPhase = action.payload;
    },
    // Reducer to set the trigger for the free spin outro out phase.
    setTriggerFreeSpinOutroOutPhase: (state, action) => {
      state.triggerFreeSpinOutroOutPhase = action.payload;
    },
    // Reducer to set the big win animation started state.
    setBigWinAnimationStarted: (state, action) => {
      state.bigWinAnimationStarted = action.payload;
    },
    // Reducer to set the big win animation completed state.
    setBigWinAnimationCompleted: (state, action) => {
      state.bigWinAnimationCompleted = action.payload;
    },
    // Reducer to set the free spin started state.
    setFreeSpinStarted: (state, action) => {
      state.freeSpinStarted = action.payload;
    },
    // Reducer to set the free spin completed state.
    setFreeSpinCompleted: (state, action) => {
      state.freeSpinCompleted = action.payload;
    },
    // Reducer to reset the state to its initial state.
    resetAnimationsState: (state) => {
      return initialState;
    },
    // Reducer to reset the info animation state.
    resetInfoAnimation: (state) => {
      state.freeSpinInfoAnimationStarted = false;
      state.freeSpinInfoAnimationCompleted = false;
      state.triggerFreeSpinInfoOutPhase = false;
    },
  },
});

// Exporting the action creators generated by createSlice for each reducer.
export const {
  setShowFreeSpinIntro,
  setShowFreeSpinOutro,
  setShowFreeSpinInfo,
  setFreeSpinIntroAnimationStarted,
  setFreeSpinIntroAnimationCompleted,
  setFreeSpinInfoAnimationStarted,
  setFreeSpinInfoAnimationCompleted,
  setFreeSpinOutroAnimationStarted,
  setFreeSpinOutroAnimationCompleted,
  setTriggerFreeSpinIntroOutPhase,
  setTriggerFreeSpinInfoOutPhase,
  setTriggerFreeSpinOutroOutPhase,
  setBigWinAnimationStarted,
  setBigWinAnimationCompleted,
  setFreeSpinStarted,
  setFreeSpinCompleted,
  resetAnimationsState,
  resetInfoAnimation,
} = animationsSlice.actions;

// Exporting the reducer function generated by createSlice to be used in the Redux store.
export default animationsSlice.reducer;
